<script lang="ts">
  import type { FormField } from '../types';
  import { resetField } from '$lib/helpers/form-field-reset';
  import countryDetectionHelper from '$lib/helpers/country-detection-helper';

  import USFlag from 'public-web-icons/dist/flags/US.png';
  import GBFlag from 'public-web-icons/dist/flags/GB.png';
  import AUFlag from 'public-web-icons/dist/flags/AU.png';
  import CAFlag from 'public-web-icons/dist/flags/CA.png';
  import JPFlag from 'public-web-icons/dist/flags/JP.png';
  import IEFlag from 'public-web-icons/dist/flags/IE.png';
  import ESFlag from 'public-web-icons/dist/flags/ES.png';
  import FRFlag from 'public-web-icons/dist/flags/FR.png';

  export let localizedCountryNames: Record<string, string>;

  const countriesObject: Record<string, any> = {
    US: {
      countryCode: 'US',
      countryName: localizedCountryNames['us'],
      flag: USFlag,
      countryNumberPlaceholder: '000 000 0000',
      countryNumberCode: '+1',
    },
    GB: {
      countryCode: 'GB',
      countryName: localizedCountryNames['gb'],
      flag: GBFlag,
      countryNumberPlaceholder: '0000 000000',
      countryNumberCode: '+44',
    },
    AU: {
      countryCode: 'AU',
      countryName: localizedCountryNames['au'],
      flag: AUFlag,
      countryNumberPlaceholder: '0000-000-000',
      countryNumberCode: '+61',
    },
    CA: {
      countryCode: 'CA',
      countryName: localizedCountryNames['ca'],
      flag: CAFlag,
      countryNumberPlaceholder: '000 000 0000',
      countryNumberCode: '+1',
    },
    JP: {
      countryCode: 'JP',
      countryName: localizedCountryNames['jp'],
      flag: JPFlag,
      countryNumberPlaceholder: '00 0000 0000',
      countryNumberCode: '+81',
    },
    IE: {
      countryCode: 'IE',
      countryName: localizedCountryNames['ie'],
      flag: IEFlag,
      countryNumberPlaceholder: '0000000000',
      countryNumberCode: '+353',
    },
    ES: {
      countryCode: 'ES',
      countryName: localizedCountryNames['es'],
      flag: ESFlag,
      countryNumberPlaceholder: '000000000',
      countryNumberCode: '+34',
    },
    FR: {
      countryCode: 'FR',
      countryName: localizedCountryNames['fr'],
      flag: FRFlag,
      countryNumberPlaceholder: '00 00 00 00 00',
      countryNumberCode: '+33',
    },
  };

  export let field: FormField;
  export let inputPhoneNumberDigitsOnly: string;
  export let isError: boolean = false;
  export let locale: string = '';

  const defaultLocale = countryDetectionHelper.extractCountryCodeFromLocale(locale);

  let rawPhoneNumberInput: HTMLInputElement;
  let selectDropdown: HTMLDivElement;

  let selectedCountry = countriesObject[defaultLocale.toUpperCase()] || countriesObject.US;
  let inputPhoneNumber = field.autofillValue || '';
  let isOpen: boolean = false;

  $: inputPhoneNumberDigitsOnly = inputPhoneNumber.replaceAll(/\D+/g, '');
  $: fullPhoneNumber = selectedCountry.countryNumberCode + inputPhoneNumberDigitsOnly;

  function handleClick() {
    rawPhoneNumberInput.focus();
  }

  function toggleDropdown() {
    isOpen ? closeDropdown() : openDropdown();
  }

  function openDropdown() {
    // close any open select since stopPropagation is applied to toggleDropdown
    // keeping only 1 close dropdown event on body click is open
    document.body.dispatchEvent(new Event('click'));
    isOpen = true;
    document.body.addEventListener('click', closeDropdown);
  }

  function closeDropdown() {
    isOpen = false;
    document.body.removeEventListener('click', closeDropdown);
  }

  function setCountry(countryCode: string | undefined) {
    if (countryCode) {
      selectedCountry = countriesObject[countryCode];
    }
  }

  /* eslint-disable no-fallthrough */
  function handleInputTriggerKeydown(e: KeyboardEvent & { currentTarget: EventTarget & HTMLButtonElement }): any {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case ' ': {
        e.preventDefault();
        selectDropdown.querySelector<HTMLElement>(`#prefixFor${selectedCountry.countryCode}`)?.focus();
        openDropdown();
        break;
      }
      case 'Escape': {
        e.preventDefault();
      }
      case 'Tab': {
        closeDropdown();
      }
    }
  }

  function handleDropdownKeydown(e: KeyboardEvent & { currentTarget: EventTarget & HTMLDivElement }): any {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        (document.activeElement?.nextElementSibling as HTMLElement)?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        (document.activeElement?.previousElementSibling as HTMLElement)?.focus();
        break;
      }
      case 'Enter':
      case ' ': {
        e?.target?.dispatchEvent(new Event('click'));
      }
      case 'Escape': {
        e.preventDefault();
        rawPhoneNumberInput.focus();
      }
      case 'Tab': {
        closeDropdown();
        break;
      }
    }
  }
  /* eslint-enable no-fallthrough */
</script>

<fieldset class="marketInput" class:isError on:click={handleClick} aria-expanded={isOpen}>
  <button
    type="button"
    class="fs-exclude countryCodeTrigger"
    aria-expanded={isOpen}
    on:click|stopPropagation={toggleDropdown}
    on:keydown={handleInputTriggerKeydown}
  >
    <span class="selectedFlag flagIcon">
      <img src={selectedCountry.flag} alt={selectedCountry.countryName} loading="lazy" />
    </span>
    <span class="arrowIcon">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="14" height="14">
        <path d="M1 5l6 6 6-6" fill="none" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </span>
  </button>
  <div
    class="intlSelectDropdown"
    role="radiogroup"
    on:keydown={handleDropdownKeydown}
    bind:this={selectDropdown}
    tabindex="0"
  >
    <div class="intlSelectScroll">
      {#each Object.entries(countriesObject) as [countryCode, countryObject]}
        <!-- this is fine because the outer div handles using the keyboard to navigate dropdown -->
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          class="fs-exclude intlSelectOption"
          id="prefixFor{countryCode}"
          role="radio"
          tabindex="-1"
          aria-checked={selectedCountry.countryCode === countryCode}
          on:click={() => setCountry(countryCode)}
        >
          <label for="prefixFor{countryCode}" id="labelFor{countryCode}" class="selectOptionLabel">
            <span class="flagIcon">
              <!-- don't need alt text, screen reader sees the label -->
              <img src={countryObject.flag} alt="" loading="lazy" />
            </span>
            <span class="countryOptionLabel">{countryObject.countryName}</span>
            <span class="countryOptionCode">{countryObject.countryNumberCode}</span>
          </label>
        </div>
      {/each}
    </div>
  </div>
  <div class="inputContainer">
    <input
      bind:this={rawPhoneNumberInput}
      bind:value={inputPhoneNumber}
      class="fs-exclude phoneNumberInput"
      type="tel"
      placeholder={selectedCountry.countryNumberPlaceholder}
      autocomplete={field.autocompleteType}
      title={field.validationMessage}
      required={field.required}
      use:resetField
    />
    {#if field.label}
      <label for={field.id}>{field.label || field.placeholder}</label>
    {/if}
    <!--Actual input that holds the full number-->
    <input bind:value={fullPhoneNumber} class="fs-exclude" type="hidden" id={field.id} name={field.id} />
  </div>
</fieldset>

<style lang="scss">
  @layer standard-components {
    .marketInput {
      height: var(--field-input-height);
      padding-left: 8px;

      .inputContainer {
        display: flex;
        flex-flow: column-reverse;
        flex-grow: 1;
        margin-left: 8px;

        label {
          bottom: 4px;
          position: relative;
        }
      }

      .countryCodeTrigger {
        cursor: pointer;
        outline: none;
        background-color: transparent;
        border: none;
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        margin-right: var(--space-base);

        &:hover {
          background: #cce1ff;
          border-radius: 6px;
        }

        .arrowIcon {
          flex-shrink: 0;
          width: 14px;
          line-height: 1;
          display: block;
          stroke: var(--color-gray-55);
        }
      }

      .flagIcon {
        display: inline-block;
        height: 18px;
        width: 32px;
        margin-right: var(--field-float-phase-horizontal-padding-size);
        vertical-align: middle;
        outline: 1px solid rgba(128, 128, 128, 0.25);
        align-self: center;
      }

      .intlSelectDropdown {
        z-index: 200;
        position: absolute;
        top: calc(100% - -7px);
        width: 100%;
        left: 0;
        background-color: white;
        opacity: 0;
        visibility: hidden;
        overflow: hidden;
        border-radius: 6px;
        transition:
          opacity 0.3s cubic-bezier(0, 0, 0.2, 1),
          visibility 0.3s cubic-bezier(0, 0, 0.2, 1);
        background-color: rgba(255, 255, 255, 1);
        box-shadow:
          rgba(0, 0, 0, 0.08) 0px 2px 16px,
          rgba(0, 0, 0, 0.08) 0px 4px 8px;

        &::after {
          content: '';
          pointer-events: none;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 0 0 5px 5px;
        }
      }

      .intlSelectScroll {
        overflow-y: auto;
        max-height: 300px;
        padding: 12px;
      }

      .intlSelectOption {
        padding: 12px;
        height: 56px;
        display: flex;
        align-items: center;
        border-radius: 6px;
        cursor: pointer;

        &:focus,
        &:hover {
          outline: none;
          background-color: #e6f0ff;
        }

        .selectOptionLabel {
          font-weight: var(--font-weight-medium);
          color: var(--field-focus-state-float-phase-label-color);
          letter-spacing: 0;
          transform-origin: 0px;
          font-size: var(--field-input-size);
          transform: none;
          cursor: pointer;
          position: static;
        }

        &[aria-checked='true'] {
          background-color: #e6f0ff;
          .selectOptionLabel {
            color: #005ad9;
          }
        }
      }

      &[aria-expanded='true'] {
        .intlSelectDropdown {
          opacity: 1;
          visibility: visible;
        }

        .arrowIcon {
          transform: rotateZ(180deg);
        }

        .fieldLabel {
          font-weight: var(--field-float-phase-label-text-weight);
          transform: scale(
            calc(var(--field-float-phase-label-text-size-unitless) / var(--field-empty-phase-label-text-size-unitless))
          );
          color: var(--field-focus-state-float-phase-label-color);
        }

        .countryOptionLabel,
        .countryOptionCode {
          position: relative;
          top: 1px;
        }

        .countryCodeTrigger {
          background: #cce1ff;
          border-radius: 6px;
        }
      }
    }
  }
</style>
